/**
 * Demo scenario — self-contained showcase of the full investigation pipeline.
 *
 * Triggered when the analyst types a Baltic-related query in demo mode.
 * Runs through all 9 pipeline states with realistic timing, inserts
 * multi-source entities with varied NATO ratings, and emits all the
 * same events the real pipeline emits.
 *
 * The real pipeline is completely untouched — this module is a parallel
 * path that produces the same output format.
 *
 * Recommended demo query: "Russian shadow fleet activity in the Baltic Sea"
 */

import { eventBus } from '@/core/events';
import { entityStore } from '@/intelligence/entities/entity-store';
import type {
  Entity,
  EntityType,
  Provenance,
  Corroboration,
  RelationshipType,
} from '@/intelligence/entities/types';
import type {
  Investigation,
  InvestigationState,
  InvestigationTierName,
  ScopeContract,
  CoverageReport,
  DraftAssessment,
  ResourceState,
} from './types';
import { INVESTIGATION_TIERS } from './types';
import {
  createInvestigation,
  getInvestigation,
  updateInvestigation,
  addLogEntry,
  linkEntityToInvestigation,
} from './investigation-store';

// ---------------------------------------------------------------------------
// Trigger detection
// ---------------------------------------------------------------------------

/**
 * Exact trigger phrase for the demo scenario.
 * Type this verbatim in the investigation bar to start the showcase.
 */
export const DEMO_QUERY = 'Baltic Shadow Fleet';

/**
 * Check if a query should trigger the demo scenario.
 * Only matches the exact trigger phrase (case-insensitive).
 */
export function isDemoQuery(query: string): boolean {
  return query.trim().toLowerCase() === DEMO_QUERY.toLowerCase();
}

// ---------------------------------------------------------------------------
// Timing
// ---------------------------------------------------------------------------

function sleep(ms: number): Promise<void> {
  return new Promise(resolve => setTimeout(resolve, ms));
}

/** Phase delays (ms) — tuned for a ~30 second total demo. */
const PHASE_DELAYS: Record<string, number> = {
  decompose: 2000,
  collect_per_task: 2500,
  extract_per_entity: 400,
  validate: 1500,
  graph_build: 2000,
  corroborate: 2500,
  synthesize: 3000,
  review: 500,
};

// ---------------------------------------------------------------------------
// Demo scope contract
// ---------------------------------------------------------------------------

export const DEMO_SCOPE: ScopeContract = {
  subject: {
    type: 'ASSET',
    subtype: 'vessel',
    description: 'Russian naval vessels and shadow fleet tankers operating in the Baltic Sea region',
    watchlistMatches: ['Baltic Fleet', 'Sovcomflot'],
  },
  behaviorIndicators: [
    { type: 'unusual_movement', threshold: 'deviation from normal shipping lanes' },
    { type: 'ais_anomaly', threshold: 'AIS transponder gaps > 4 hours' },
    { type: 'proximity', threshold: 'within 12nm of critical infrastructure' },
  ],
  geographicBounds: {
    source: 'desk_config',
    bbox: [9.0, 53.0, 30.0, 66.0],
    override: false,
  },
  temporalBounds: {
    start: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString(),
    end: new Date().toISOString(),
    source: 'query_implied_recent',
  },
  exclusions: ['civilian_ferry_traffic', 'routine_cargo'],
};

// ---------------------------------------------------------------------------
// Demo entities — multi-source with realistic provenance
// ---------------------------------------------------------------------------

interface DemoEntityDef {
  type: EntityType;
  name: string;
  aliases: string[];
  description: string;
  properties: Record<string, unknown>;
  provenance: Provenance;
  corroboration: Corroboration;
  notes: string[];
}

const DEMO_ENTITIES: DemoEntityDef[] = [
  {
    type: 'ASSET',
    name: 'NS Concord',
    aliases: ['NS CONCORD', 'IMO 9305027'],
    description: 'Russian-flagged tanker suspected of shadow fleet operations in the Baltic',
    properties: { flag: 'Russia', vesselType: 'crude oil tanker', imo: '9305027', mmsi: '273459120', identifiers: [{ type: 'IMO', value: '9305027' }, { type: 'MMSI', value: '273459120' }] },
    provenance: { sourceUrl: 'https://lloydslist.maritimeintelligence.informa.com', extractionQuote: 'The NS Concord, a Russian-flagged tanker, was observed transiting the Danish Straits with its AIS transponder intermittently active', collectorId: 'collector_src_gdelt', collectedAt: '', confidence: 0.88 },
    corroboration: { count: 3, sources: ['lloydslist.com', 'marinetraffic.com', 'balticosint'], natoRating: 'B2' },
    notes: ['Flagged by Baltic OSINT channel as potential sanctions evasion vessel', 'AIS pattern matches known shadow fleet cycling behavior (~45min intervals)'],
  },
  {
    type: 'ASSET',
    name: 'Admiral Gorshkov',
    aliases: ['Адмирал Горшков', 'Hull 454'],
    description: 'Russian Navy frigate, Project 22350, Northern Fleet',
    properties: { flag: 'Russia', vesselType: 'frigate', hullNumber: '454', class: 'Admiral Gorshkov-class' },
    provenance: { sourceUrl: 'https://www.reuters.com/world/europe', extractionQuote: 'Russian frigate Admiral Gorshkov conducted exercises south of Gotland', collectorId: 'collector_src_reuters_europe', collectedAt: '', confidence: 0.92 },
    corroboration: { count: 3, sources: ['reuters.com', 'janes.com', 'svt.se'], natoRating: 'A2' },
    notes: ['Project 22350 frigate — carries Zircon hypersonic missiles'],
  },
  {
    type: 'LOCATION',
    name: 'Gotland',
    aliases: ['Gotland Island'],
    description: 'Swedish island in the Baltic Sea, strategic military significance',
    properties: { country: 'Sweden', coordinates: { lat: 57.5, lng: 18.5 } },
    provenance: { sourceUrl: 'https://www.reuters.com/world/europe', extractionQuote: 'naval activity was concentrated in waters south of Gotland', collectorId: 'collector_src_reuters_europe', collectedAt: '', confidence: 0.95 },
    corroboration: { count: 4, sources: ['reuters.com', 'svt.se', 'marinetraffic.com', 'balticosint'], natoRating: 'A1' },
    notes: [],
  },
  {
    type: 'ORGANIZATION',
    name: 'Russian Northern Fleet',
    aliases: ['Северный флот', 'SF', 'Northern Fleet'],
    description: 'Russian Navy fleet based in Severomorsk, responsible for Arctic and North Atlantic operations',
    properties: { headquarters: 'Severomorsk', branch: 'Russian Navy' },
    provenance: { sourceUrl: 'https://www.janes.com/defence-news', extractionQuote: 'Northern Fleet assets deployed to the Baltic for scheduled exercises', collectorId: 'collector_src_rss_janes', collectedAt: '', confidence: 0.90 },
    corroboration: { count: 2, sources: ['janes.com', 'reuters.com'], natoRating: 'B2' },
    notes: [],
  },
  {
    type: 'LOCATION',
    name: 'Danish Straits',
    aliases: ['Danish Belts', 'Øresund'],
    description: 'Maritime chokepoint connecting the Baltic Sea to the North Sea',
    properties: { type: 'chokepoint' },
    provenance: { sourceUrl: 'https://lloydslist.maritimeintelligence.informa.com', extractionQuote: 'transiting the Danish Straits with its AIS transponder intermittently active', collectorId: 'collector_src_gdelt', collectedAt: '', confidence: 0.93 },
    corroboration: { count: 2, sources: ['lloydslist.com', 'marinetraffic.com'], natoRating: 'B2' },
    notes: [],
  },
  {
    type: 'ORGANIZATION',
    name: 'Swedish Armed Forces',
    aliases: ['Försvarsmakten'],
    description: 'Military forces of Sweden, increased Baltic readiness since 2022',
    properties: { country: 'Sweden' },
    provenance: { sourceUrl: 'https://www.svt.se/nyheter', extractionQuote: 'Swedish Armed Forces raised readiness level for naval units near Gotland', collectorId: 'collector_src_rss_svt', collectedAt: '', confidence: 0.85 },
    corroboration: { count: 2, sources: ['svt.se', 'reuters.com'], natoRating: 'A2' },
    notes: [],
  },
  {
    type: 'PERSON',
    name: 'Admiral Nikolai Yevmenov',
    aliases: ['Yevmenov'],
    description: 'Commander of the Russian Navy',
    properties: { rank: 'Admiral', role: 'Commander-in-Chief, Russian Navy' },
    provenance: { sourceUrl: 'https://www.janes.com/defence-news', extractionQuote: 'Admiral Yevmenov authorized the deployment of additional surface combatants', collectorId: 'collector_src_rss_janes', collectedAt: '', confidence: 0.78 },
    corroboration: { count: 1, sources: ['janes.com'], natoRating: 'C4' },
    notes: [],
  },
  {
    type: 'ASSET',
    name: 'Nord Stream Pipeline Route',
    aliases: ['Nord Stream'],
    description: 'Subsea gas pipeline route crossing the Baltic Sea',
    properties: { type: 'infrastructure', status: 'damaged' },
    provenance: { sourceUrl: 'https://lloydslist.maritimeintelligence.informa.com', extractionQuote: 'shadow fleet vessel observed loitering near the Nord Stream pipeline route', collectorId: 'collector_src_gdelt', collectedAt: '', confidence: 0.82 },
    corroboration: { count: 2, sources: ['lloydslist.com', 'balticosint'], natoRating: 'C3' },
    notes: ['Proximity to damaged pipeline infrastructure raises surveillance concerns'],
  },
  {
    type: 'EVENT',
    name: 'Baltic AIS Disruption Event',
    aliases: [],
    description: 'Coordinated AIS signal gaps detected across multiple vessels in the central Baltic',
    properties: { startDate: new Date(Date.now() - 3 * 24 * 60 * 60 * 1000).toISOString(), affectedVessels: 4 },
    provenance: { sourceUrl: 'https://www.marinetraffic.com/en/ais/intelligence', extractionQuote: 'multiple vessels simultaneously lost AIS contact in the central Baltic, consistent with electronic warfare activity', collectorId: 'collector_src_marinetraffic', collectedAt: '', confidence: 0.75 },
    corroboration: { count: 2, sources: ['marinetraffic.com', 'balticosint'], natoRating: 'C3' },
    notes: [],
  },
  {
    type: 'LOCATION',
    name: 'Kaliningrad',
    aliases: ['Kaliningrad Oblast'],
    description: 'Russian exclave on the Baltic Sea, major naval base',
    properties: { country: 'Russia', militaryAssets: ['Baltic Fleet HQ', 'Iskander missiles'] },
    provenance: { sourceUrl: 'https://www.janes.com/defence-news', extractionQuote: 'vessels departed from Baltiysk naval base in Kaliningrad', collectorId: 'collector_src_rss_janes', collectedAt: '', confidence: 0.91 },
    corroboration: { count: 3, sources: ['janes.com', 'reuters.com', 'svt.se'], natoRating: 'A1' },
    notes: [],
  },
  {
    type: 'INDICATOR',
    name: 'Shadow Fleet AIS Pattern',
    aliases: [],
    description: 'Behavioral indicator: vessels switching AIS on/off at regular intervals to avoid tracking',
    properties: { pattern: 'cyclic_ais_gaps', intervalMinutes: 45 },
    provenance: { sourceUrl: 'https://www.marinetraffic.com/en/ais/intelligence', extractionQuote: 'characteristic shadow fleet pattern of 45-minute AIS gaps followed by brief transmissions', collectorId: 'collector_src_marinetraffic', collectedAt: '', confidence: 0.80 },
    corroboration: { count: 2, sources: ['marinetraffic.com', 'lloydslist.com'], natoRating: 'B3' },
    notes: [],
  },
  {
    type: 'ASSET',
    name: 'Baltiysk Naval Base',
    aliases: ['Baltiysk'],
    description: 'Main base of the Russian Baltic Fleet',
    properties: { country: 'Russia', fleet: 'Baltic Fleet', type: 'naval_base' },
    provenance: { sourceUrl: 'https://www.janes.com/defence-news', extractionQuote: 'vessels departed from Baltiysk naval base', collectorId: 'collector_src_rss_janes', collectedAt: '', confidence: 0.94 },
    corroboration: { count: 3, sources: ['janes.com', 'reuters.com', 'svt.se'], natoRating: 'A1' },
    notes: [],
  },
];

// ---------------------------------------------------------------------------
// Demo relationships
// ---------------------------------------------------------------------------

interface DemoRelDef {
  sourceName: string;
  targetName: string;
  type: RelationshipType;
  confidence: number;
  evidenceQuote: string;
}

const DEMO_RELATIONSHIPS: DemoRelDef[] = [
  { sourceName: 'Admiral Gorshkov', targetName: 'Russian Northern Fleet', type: 'affiliated_with', confidence: 0.95, evidenceQuote: 'Northern Fleet frigate Admiral Gorshkov' },
  { sourceName: 'Admiral Gorshkov', targetName: 'Gotland', type: 'deployed_to', confidence: 0.88, evidenceQuote: 'conducted exercises south of Gotland' },
  { sourceName: 'NS Concord', targetName: 'Danish Straits', type: 'located_at', confidence: 0.90, evidenceQuote: 'transiting the Danish Straits' },
  { sourceName: 'NS Concord', targetName: 'Shadow Fleet AIS Pattern', type: 'associated_with', confidence: 0.82, evidenceQuote: 'AIS transponder intermittently active' },
  { sourceName: 'NS Concord', targetName: 'Nord Stream Pipeline Route', type: 'observed_at', confidence: 0.72, evidenceQuote: 'observed loitering near the Nord Stream pipeline route' },
  { sourceName: 'Admiral Nikolai Yevmenov', targetName: 'Russian Northern Fleet', type: 'commands', confidence: 0.85, evidenceQuote: 'Admiral Yevmenov authorized the deployment' },
  { sourceName: 'Swedish Armed Forces', targetName: 'Gotland', type: 'deployed_to', confidence: 0.80, evidenceQuote: 'raised readiness level for naval units near Gotland' },
  { sourceName: 'Russian Northern Fleet', targetName: 'Baltiysk Naval Base', type: 'based_at', confidence: 0.75, evidenceQuote: 'vessels departed from Baltiysk naval base' },
  { sourceName: 'Baltic AIS Disruption Event', targetName: 'Gotland', type: 'located_at', confidence: 0.78, evidenceQuote: 'central Baltic' },
  { sourceName: 'Kaliningrad', targetName: 'Baltiysk Naval Base', type: 'located_at', confidence: 0.96, evidenceQuote: 'Baltiysk naval base in Kaliningrad' },
];

// ---------------------------------------------------------------------------
// Demo assessment
// ---------------------------------------------------------------------------

const DEMO_ASSESSMENT = `## Baltic Maritime Intelligence Assessment

### Summary

Analysis of open-source intelligence indicates heightened Russian naval activity in the Baltic Sea over the past 7 days, centered around the waters south of Gotland and the Danish Straits. Two primary threads of activity have been identified:

1. **Military exercises**: The Northern Fleet frigate Admiral Gorshkov conducted exercises south of Gotland, representing an unusual deployment of a high-capability surface combatant outside its normal operating area.

2. **Shadow fleet activity**: The Russian-flagged tanker NS Concord exhibited characteristic shadow fleet behavior (cyclic AIS gaps at ~45-minute intervals) while transiting the Danish Straits and was subsequently observed loitering near the Nord Stream pipeline route.

### Key Findings

- **12 entities** extracted across 5 independent sources with cross-source corroboration
- **NATO Rating B2** (Usually Reliable / Probably True) for the core military movement assessment — corroborated by Reuters, Janes, and SVT
- **NATO Rating C3** (Fairly Reliable / Possibly True) for the shadow fleet infrastructure threat assessment — corroborated by Lloyd's List and Baltic OSINT
- Swedish Armed Forces responded by raising readiness levels for naval units near Gotland (A2 — confirmed by SVT and Reuters)
- AIS disruption event affecting 4 vessels near Gotland consistent with electronic warfare activity from Kaliningrad direction

### Gaps & Limitations

- No submarine activity data available from open sources
- AIS data has inherent gaps that may not all be intentional
- Limited visibility into Russian military communications
- Admiral Yevmenov's role based on single source (C4 — unconfirmed)

### Recommended Follow-up

- Monitor NS Concord's next port call for cargo verification
- Cross-reference with satellite imagery of Gotland waters
- Track additional Northern Fleet deployments from Severomorsk
- Investigate MMSI 273459120 history for previous shadow fleet patterns`;

// ---------------------------------------------------------------------------
// Demo coverage report
// ---------------------------------------------------------------------------

const DEMO_COVERAGE: CoverageReport = {
  parameters: [
    { name: 'Subject identified', addressed: true, corroborated: true, detail: 'NS Concord + Admiral Gorshkov identified' },
    { name: 'Shadow fleet behavior', addressed: true, corroborated: true, detail: 'AIS cycling pattern confirmed by 2 sources' },
    { name: 'AIS anomalies', addressed: true, corroborated: true, detail: '4 vessels affected in disruption event' },
    { name: 'Infrastructure proximity', addressed: true, corroborated: false, detail: 'Nord Stream proximity from single source' },
    { name: 'Military movement', addressed: true, corroborated: true, detail: 'Northern Fleet deployment from 3 sources' },
    { name: 'Geographic bounds (Baltic)', addressed: true, corroborated: true, detail: 'All entities within Baltic bbox' },
    { name: 'Temporal bounds (7 days)', addressed: true, corroborated: true, detail: 'All events within 7-day window' },
  ],
  overallPercentage: 92,
  sufficientForSynthesis: true,
};

// ---------------------------------------------------------------------------
// Runner
// ---------------------------------------------------------------------------

/**
 * Run the full demo scenario with realistic timing and events.
 * Called by investigation-runner when a demo query is detected.
 *
 * This mimics the orchestrator's state machine by:
 * 1. Creating a real investigation in SQLite
 * 2. Transitioning through all 9 states with delays
 * 3. Inserting real entities and relationships
 * 4. Emitting all the same events the real pipeline emits
 *
 * The result is indistinguishable from a real investigation in the UI.
 */
/** Store the investigation + tier so runDemoScenarioAfterConfirm can use them. */
let _demoInvestigation: Investigation | null = null;
let _demoTier: InvestigationTierName = 'STANDARD';

/**
 * Get the demo investigation (for the runner to show confirmation UI).
 */
export function getDemoInvestigation(): Investigation | null {
  return _demoInvestigation;
}

export async function runDemoScenario(
  query: string,
  tier: InvestigationTierName,
): Promise<void> {
  const ts = new Date().toISOString();
  _demoTier = tier;

  // Set collected timestamps relative to now
  for (const entity of DEMO_ENTITIES) {
    entity.provenance.collectedAt = ts;
  }

  // --- INIT ---
  const investigation = await createInvestigation({
    title: query,
    rawQuery: query,
    deskId: 'baltic-maritime-awareness',
    tier,
  });
  _demoInvestigation = investigation;
  eventBus.emit('investigation:start', investigation);

  await emitState(investigation.id, 'init', 'decompose');
  await log(investigation.id, 'decompose', 'state_transition', { from: 'init', to: 'decompose' });

  // --- DECOMPOSE ---
  await sleep(PHASE_DELAYS.decompose);
  await updateInvestigation(investigation.id, {
    decomposed: DEMO_SCOPE,
    scopeContract: DEMO_SCOPE,
  });

  await log(investigation.id, 'decompose', 'llm_call', {
    purpose: 'query_decomposition', model: 'demo-sonnet', tokensUsed: 1250,
  });

  // The runner will show confirmation UI using getDemoInvestigation()
  // and call runDemoScenarioAfterConfirm() on confirm.
}

/**
 * Continue the demo scenario after the analyst confirms the scope.
 * Called by investigation-runner's onConfirm callback.
 */
export async function runDemoScenarioAfterConfirm(
  investigationId: string,
): Promise<void> {
  const startTime = Date.now();
  const ts = new Date().toISOString();

  // Update timestamps
  for (const entity of DEMO_ENTITIES) {
    entity.provenance.collectedAt = ts;
  }

  // --- COLLECT ---
  await emitState(investigationId, 'decompose', 'collect');
  await log(investigationId, 'collect', 'state_transition', { from: 'decompose', to: 'collect' });

  // Simulate 4 collection tasks completing
  const sources = ['GDELT', 'Reuters RSS', 'MarineTraffic API', 'Baltic OSINT Telegram'];
  for (let i = 0; i < sources.length; i++) {
    await sleep(PHASE_DELAYS.collect_per_task);
    await log(investigationId, 'collect', 'task_completed', {
      taskId: `demo_task_${i}`, sourceId: sources[i], status: 'completed',
      rawDataLength: 1500 + Math.floor(Math.random() * 2000),
    });
    eventBus.emit('investigation:task-completed', {
      investigationId,
      task: {
        id: `demo_task_${i}`,
        investigationId,
        sourceId: sources[i],
        query: { type: 'demo' },
        expectedEntityTypes: ['ASSET', 'LOCATION', 'ORGANIZATION', 'PERSON', 'EVENT', 'INDICATOR'],
        status: 'completed',
        rawData: `Demo collection data from ${sources[i]}`,
        startedAt: ts,
        completedAt: new Date().toISOString(),
        error: null,
      },
    });
  }

  // --- EXTRACT ---
  await emitState(investigationId, 'collect', 'extract');
  await log(investigationId, 'extract', 'state_transition', { from: 'collect', to: 'extract' });

  // Insert entities one by one with progressive disclosure
  const entityIdMap = new Map<string, string>();
  let tokenTotal = 1250; // from decomposition

  for (const def of DEMO_ENTITIES) {
    await sleep(PHASE_DELAYS.extract_per_entity);

    const entity = await entityStore.create({
      type: def.type,
      name: def.name,
      aliases: def.aliases,
      description: def.description,
      properties: def.properties,
      provenance: def.provenance,
      corroboration: def.corroboration,
      sources: [],
      relationships: [],
      firstSeen: def.provenance.collectedAt,
      lastSeen: def.provenance.collectedAt,
      confidence: def.provenance.confidence,
      tags: [],
      notes: def.notes,
      watchStatus: 'none',
    });

    entityIdMap.set(def.name, entity.id);
    await linkEntityToInvestigation(investigationId, entity.id);

    tokenTotal += 320;

    // Progressive disclosure — entity appears in the graph
    eventBus.emit('investigation:entity-extracted', {
      investigationId,
      entity: entity as Entity,
    });

    await log(investigationId, 'extract', 'entity_extracted', {
      entityId: entity.id, entityName: entity.name, entityType: entity.type,
      confidence: def.provenance.confidence,
    });

    // Resource update
    const tierConfig = INVESTIGATION_TIERS[_demoTier];
    const resource: ResourceState = {
      tokensUsed: tokenTotal,
      tokenBudget: tierConfig.tokenBudget,
      tokensPercent: Math.round((tokenTotal / tierConfig.tokenBudget) * 100),
      nodeCount: entityIdMap.size,
      nodeCeiling: tierConfig.nodeCeiling,
      edgeCount: 0,
      edgeCeiling: tierConfig.edgeCeiling,
      elapsedSeconds: Math.round((Date.now() - startTime) / 1000),
      timeLimitSeconds: tierConfig.timeLimitSeconds,
      coveragePercent: Math.round((entityIdMap.size / DEMO_ENTITIES.length) * 100),
      infoGainTrend: [],
      diminishingReturns: false,
    };
    eventBus.emit('investigation:resource-update', {
      investigationId,
      resource,
    });
  }

  // --- VALIDATE ---
  await emitState(investigationId, 'extract', 'validate');
  await log(investigationId, 'validate', 'state_transition', { from: 'extract', to: 'validate' });
  await sleep(PHASE_DELAYS.validate);
  await log(investigationId, 'validate', 'validation_pass', {
    total: DEMO_ENTITIES.length, valid: DEMO_ENTITIES.length, rejected: 0,
  });

  // --- GRAPH BUILD ---
  await emitState(investigationId, 'validate', 'graph_build');
  await log(investigationId, 'graph_build', 'state_transition', { from: 'validate', to: 'graph_build' });
  await sleep(PHASE_DELAYS.graph_build);

  // Insert relationships
  for (const rel of DEMO_RELATIONSHIPS) {
    const sourceId = entityIdMap.get(rel.sourceName);
    const targetId = entityIdMap.get(rel.targetName);
    if (!sourceId || !targetId) continue;

    await entityStore.addRelationship({
      sourceEntityId: sourceId,
      targetEntityId: targetId,
      type: rel.type,
      confidence: rel.confidence,
      evidence: [{
        sourceId: 'demo', eventId: 'demo', timestamp: new Date().toISOString(),
        snippet: rel.evidenceQuote, confidence: rel.confidence,
      }],
      firstSeen: new Date().toISOString(),
      lastSeen: new Date().toISOString(),
    });
  }

  tokenTotal += 2400;
  await log(investigationId, 'graph_build', 'llm_call', {
    purpose: 'relationship_identification', model: 'demo-sonnet',
    tokensUsed: 2400, relationships: DEMO_RELATIONSHIPS.length,
  });

  // --- CORROBORATE ---
  await emitState(investigationId, 'graph_build', 'corroborate');
  await log(investigationId, 'corroborate', 'state_transition', { from: 'graph_build', to: 'corroborate' });
  await sleep(PHASE_DELAYS.corroborate);

  await log(investigationId, 'corroborate', 'validation_pass', {
    entitiesCorroborated: DEMO_ENTITIES.length,
    multiSource: DEMO_ENTITIES.filter(e => e.corroboration.count > 1).length,
    singleSource: DEMO_ENTITIES.filter(e => e.corroboration.count === 1).length,
  });

  // --- SYNTHESIZE ---
  await emitState(investigationId, 'corroborate', 'synthesize');
  await log(investigationId, 'synthesize', 'state_transition', { from: 'corroborate', to: 'synthesize' });
  await sleep(PHASE_DELAYS.synthesize);

  tokenTotal += 4800;
  await log(investigationId, 'synthesize', 'llm_call', {
    purpose: 'synthesis', model: 'demo-opus', tokensUsed: 4800,
  });

  const assessment: DraftAssessment = {
    investigationId,
    executiveSummary: DEMO_ASSESSMENT,
    sections: [
      { title: 'Military Activity', content: 'Northern Fleet frigate Admiral Gorshkov conducted exercises south of Gotland, representing an unusual deployment of a high-capability surface combatant outside its normal operating area.', citations: [] },
      { title: 'Shadow Fleet Operations', content: 'The NS Concord exhibited characteristic shadow fleet behavior while transiting the Danish Straits and was observed loitering near the Nord Stream pipeline route.', citations: [] },
      { title: 'Electronic Warfare', content: 'Multiple vessels simultaneously lost AIS contact in the central Baltic, consistent with electronic warfare activity from Kaliningrad direction.', citations: [] },
    ],
    generatedAt: new Date().toISOString(),
    modelUsed: 'demo-opus',
    tokenCost: 4800,
  };

  eventBus.emit('investigation:synthesis-complete', {
    investigationId,
    assessment,
  });

  // --- REVIEW ---
  await emitState(investigationId, 'synthesize', 'review');

  // Final update
  await updateInvestigation(investigationId, {
    state: 'review',
    tokenUsage: tokenTotal,
    nodeCount: DEMO_ENTITIES.length,
    edgeCount: DEMO_RELATIONSHIPS.length,
    coverage: DEMO_COVERAGE,
    completedAt: new Date().toISOString(),
  });

  // Load the full investigation for the complete event
  const inv = await getInvestigation(investigationId);
  if (inv) {
    eventBus.emit('investigation:complete', inv);
  }

  _demoInvestigation = null;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

async function emitState(
  investigationId: string,
  from: InvestigationState,
  to: InvestigationState,
): Promise<void> {
  await updateInvestigation(investigationId, { state: to });
  eventBus.emit('investigation:state', { investigationId, from, to });
}

async function log(
  investigationId: string,
  state: string,
  action: string,
  detail: Record<string, unknown>,
): Promise<void> {
  await addLogEntry({
    investigationId,
    state: state as InvestigationState,
    action: action as import('./types').LogAction,
    detail,
  });
}
